import { inject, Injectable } from '@angular/core';
import { ApiError } from '@r1-compass-library/models';
import { PlayConfigAuditSearch } from '@r1-compass-library/models/search/play-config-audit-search.model';
import { PlayConfigAuditService } from './play-config-audit.service';
import { forkJoin, of, switchMap, tap } from 'rxjs';
import { SnackBarService } from './snack-bar.service';
import { ErrorHandlingService } from './error-handling.service';
import { DivisionConfigService } from './division-config.service';
import { TemplateCriteriaService } from './template-criteria.service';
import { LifecycleRulesetService } from './lifecycle-ruleset.service';

@Injectable({
    providedIn: 'root',
})
export class PlaySupportDetailsService {
    readonly #playConfigAuditService = inject(PlayConfigAuditService);
    readonly #snackBarService = inject(SnackBarService);
    readonly #errorHandlingService = inject(ErrorHandlingService);
    readonly #divisionConfigService = inject(DivisionConfigService);
    readonly #templateCriteriaService = inject(TemplateCriteriaService);
    readonly #lifecycleRulesetService = inject(LifecycleRulesetService);

    downloadSupportData(playId: number, startDate: string, executionId: string) {
        let supportData: Blob;
        const playAuditSearch: PlayConfigAuditSearch = {
            playId: playId,
            executionStartDate: startDate,
        };

        this.#playConfigAuditService
            .findLatest(playAuditSearch)
            .pipe(
                switchMap(auditResult => {
                    if (auditResult.status !== 'Success' && auditResult.message) {
                        this.#snackBarService.openErrorSnackbar(
                            this.#errorHandlingService.displayApiError({
                                error: new ApiError({ status: 404, message: auditResult.message }),
                                type: 'snackbar',
                            })
                        );
                    }
                    return forkJoin({
                        audit: of(auditResult.playAudit),
                        division: this.#divisionConfigService.getByIamDivisionId(auditResult.playAudit.iamDivisionId),
                        templateCriteria: auditResult.playAudit.templateCriteriaId
                            ? this.#templateCriteriaService.get(auditResult.playAudit.templateCriteriaId)
                            : of(null),
                        lifecycleRuleset: auditResult.playAudit.lifecycleRulesetId
                            ? this.#lifecycleRulesetService.get(auditResult.playAudit.lifecycleRulesetId)
                            : of(null),
                    });
                }),
                tap(({ audit, division, templateCriteria, lifecycleRuleset }) => {
                    const text = JSON.stringify({
                        play: audit.config,
                        templateCriteria: templateCriteria,
                        lifecycleRule: lifecycleRuleset,
                        division: division,
                    });
                    supportData = new Blob([text], { type: 'text/json' });
                    const data = window.URL.createObjectURL(supportData);
                    const link = document.createElement('a');
                    link.href = data;
                    link.download = `${playId}_${executionId}_support_info.json`;
                    link.click();
                })
            )
            .subscribe();
    }
}
